// @flow

import _ from 'lodash/fp';
import {
  GET_REQUEST, GET_SUCCESS,
  GET_FAILURE, UPDATE_REQUEST,
  UPDATE_SUCCESS, UPDATE_FAILURE,
  CREATE_REQUEST, CREATE_SUCCESS,
  CREATE_FAILURE, SELECT_ROOM,
  SELECT_PRODUCT_SLOT, ROOM_UPDATE_SUCCESS,
  ROOM_UPDATE_REQUEST, ROOM_UPDATE_FAILURE,
  ROOM_CREATE_SUCCESS, ROOM_CREATE_REQUEST,
  ROOM_CREATE_FAILURE, PRODUCT_SLOT_UPDATE_SUCCESS,
  PRODUCT_SLOT_UPDATE_REQUEST, PRODUCT_SLOT_UPDATE_FAILURE,
  PRODUCT_SLOT_CREATE_SUCCESS, PRODUCT_SLOT_CREATE_REQUEST,
  PRODUCT_SLOT_CREATE_FAILURE, CLEAR,
  OPEN_DIALOG_REQUEST, OPEN_DIALOG_FAILURE,
  OPEN_DIALOG_SUCCESS, CLOSE_DIALOG,
  POP_DIALOG_NAVIGATION, NAVIGATE_TO_VARIANT_REQUEST,
  NAVIGATE_TO_VARIANT_FAILURE, NAVIGATE_TO_VARIANT_SUCCESS,
  UPDATE_RELATED_VARIANT, TOGGLE_VARIANT_REQUEST,
  TOGGLE_VARIANT_FAILURE, TOGGLE_VARIANT_SUCCESS,
  SET_STANDARD_PRODUCT, RESET,
  TOGGLE_PRODUCT, CHECK_ALL_VARIANTS,
  CLEAR_STANDARD_PRODUCT, CHECK_PRODUCT,
  UPDATE_VARIANTS, ROOMS_UPDATE_REQUEST,
  ROOMS_UPDATE_FAILURE, ROOMS_UPDATE_SUCCESS,
  PRODUCT_SLOTS_UPDATE_REQUEST, PRODUCT_SLOTS_UPDATE_FAILURE,
  PRODUCT_SLOTS_UPDATE_SUCCESS, UPDATE_VARIANTS_FOR_TYPE,
} from '../actions/configTemplateView';
import {SET_DIRTY} from '../actions/form';
import uuid from 'uuid';
import type {
  ConfigTemplateAction,
} from '../actions/configTemplateView';
import type {
  FormAction,
} from '../actions/form';
import type {
  ErrorDto,
  ConfigTemplateDto,
  LimitedProjectDto,
  RoomTemplateDto,
  RoomDto,
  RoomTypeDto,
} from '../dtos';

export type ConfigTemplateState = {
  active: ?any,
  error: ?ErrorDto,
  isDirty: bool,
  isFetching: bool,
};

type StandardProductsState = {
  [key: number]: ?number,
};

type ConfigEntry = {
  checked: bool,
  expanded: bool,
  id: ?number,
  parentId: ?number,
  parentUuid: ?string,
  productSlotId: ?number,
  productVariantId: number,
  quantity: ?number,
  related: any[],
  standardProducts: StandardProductsState,
  unit: ?string,
  uuid: string,
  customPrice: ?number,
};

export type ModifiedState = {
  [key: string]: ConfigEntry,
};

export type NavigationState = {
  active: any[],
  currentUuid: ?string,
  name: string,
  previous: ?NavigationState,
  standardProducts: StandardProductsState,
};

export type DialogState = {
  config: any[],
  error: ?ErrorDto,
  isFetching: bool,
  modifiedData: ModifiedState,
  navigation: ?NavigationState,
  show: bool,
  sharedChecked: ?bool,
  standardProductId: ?number,
};

export type ProductsState = any[];

export type ConfigTemplatesState = ConfigTemplateDto[];

export type ProjectsState = LimitedProjectDto[];

export type ProductSlotState = {
  active: ?any,
  dialog: DialogState,
  error: ?ErrorDto,
  isDirty: bool,
  isFetching: bool,
  roomId: ?number,
};

export type ProductTypesState = any[];

export type RoomState = {
  active: ?any,
  error: ?ErrorDto,
  isDirty: bool,
  isFetching: bool,
};

export type RoomsState = RoomDto[];

export type RoomTypesState = RoomTypeDto[];

export type RoomTemplatesState = RoomTemplateDto[];

export type State_ = {
  configTemplate: ConfigTemplateState,
  products: ProductsState,
  productSlot: ProductSlotState,
  productTypes: ProductTypesState,
  room: RoomState,
  rooms: RoomsState,
  roomTypes: RoomTypesState,
  configTemplates: ConfigTemplatesState,
  projects: ProjectsState,
  roomTemplates: RoomTemplatesState,
};

export type Exact<T> = T & $Shape<T>;

type State = {
  configTemplate: Exact<ConfigTemplateState>,
  products: ProductsState,
  productSlot: Exact<ProductSlotState>,
  productTypes: ProductTypesState,
  room: Exact<RoomState>,
  rooms: RoomsState,
  roomTypes: RoomTypesState,
  configTemplates: ConfigTemplatesState,
  projects: ProjectsState,
  roomTemplates: RoomTemplatesState,
};

const sortByName =
  _.sortBy([s => _.toLower(s.name)]);

export const sortByIndex =
  _.sortBy([x => x.sortIndex]);

export const sortRoomList: (rooms: any[]) => any[] =
  _.flow(
    _.map(r => ({
      ...r,
      productSlots: sortByIndex(r.productSlots),
    })),
    sortByIndex,
  );

const popState = (state: ?NavigationState, depth: number = 1): ?Exact<NavigationState> => {
  if (!state) {
    return null;
  } else if (depth > 0) {
    return popState(state.previous, depth - 1) || state;
  }

  return state;
};

const notArchived =
  _.filter(x => !x.archived);

const mapRelated = (config: any[], variant: any): any[] =>
  _.flow(
    _.groupBy(x => x.productTypeId),
    _.values,
    sortByName,
    _.map(([head, ...tail]) => ({
      id: head.productTypeId,
      name: head.productTypeName,
      products: _.flow(
        _.groupBy(x => x.productId),
        _.values,
        notArchived,
        sortByName,
        _.map(([head, ...tail]) => ({
          id: head.productId,
          name: head.productName,
          mainVariantId: head.mainVariantId,
          variants: _.flow(
            notArchived,
            sortByName,
            _.map(({id, name, itemNumber, hasRelated, productId, sellingPrice}) => {
              const existingConfig = _.find({
                productVariantId: id,
                parentUuid: variant.uuid,
              }, config);
              return ({
                id,
                name,
                itemNumber,
                hasRelated,
                productId,
                uuid: existingConfig ? existingConfig.uuid : uuid.v4(),
                sellingPrice,
              });
            }),
          )([head, ...tail]),
          expanded: true,
        })),
      )([head, ...tail]),
    })),
  )(variant.related);

export const handleError = (state: DialogState, action: any): Exact<DialogState> => ({
  ...state,
  error: action.error,
  isFetching: false,
});

export const openDialogRequest = (state: DialogState, action: any): Exact<DialogState> => ({
  ...state,
  isFetching: true,
  navigation: null,
  show: true,
});

export const toggleVariantRequest = (state: DialogState, action: any): Exact<DialogState> => ({
  ...state,
  isFetching: true,
});

export const navigateToVariantRequest = toggleVariantRequest;

const mapConfigEntries = (modifiedData, parentId, parentUuid, productSlot) => _.flow(
  _.flatMap(x => _.map(y => ({
    productType: x,
    product: y,
  }), x.products)),
  _.flatMap(x => _.map(y => ({
    ...x,
    variant: y,
  }), x.product.variants)),
  _.filter(x => !modifiedData[x.variant.uuid]),
  _.keyBy(x => x.variant.uuid),
  _.mapValues(x => {
    const allVariants = _.flatMap(y => y.variants, x.productType.products);

    let firstWithQuantity = _.flow(
      _.map(v => modifiedData[v.uuid]),
      _.filter(v => !!v && v.quantity !== null),
    )(x.product.variants);
    if (!firstWithQuantity.length) {
      firstWithQuantity = _.flow(
        _.map(v => modifiedData[v.uuid]),
        _.filter(v => !!v && v.quantity !== null),
      )(allVariants);
    }
    const firstQuantity = !!firstWithQuantity.length
      ? firstWithQuantity[0].quantity
      : 1;

    let firstWithUnit = _.flow(
      _.map(v => modifiedData[v.uuid]),
      _.filter(v => !!v && !!v.unit),
    )(x.product.variants);
    if (!firstWithQuantity.length) {
      firstWithUnit = _.flow(
        _.map(v => modifiedData[v.uuid]),
        _.filter(v => !!v && !!v.unit),
      )(allVariants);
    }
    const firstUnit = !!firstWithUnit.length
      ? firstWithUnit[0].unit || ''
      : '';

    return {
      checked: false,
      expanded: false,
      id: null,
      parentId,
      parentUuid,
      productId: x.variant.productId,
      productSlotId: productSlot ? productSlot.id : null,
      productVariantId: x.variant.id,
      quantity: firstQuantity,
      related: [],
      standardProducts: {},
      unit: firstUnit,
      uuid: x.variant.uuid,
      customPrice: null,
    };
  }),
);

const getSharedChecked = (productTypes, modifiedData) => {
  const variants = _.flow(
    _.flatMap(x => x.products),
    _.flatMap(x => x.variants),
  )(productTypes);
  const checkedCount = _.filter(v =>
    modifiedData[v.uuid] && modifiedData[v.uuid].checked, variants
  ).length;
  return checkedCount === variants.length
    ? true
    : checkedCount === 0
      ? false
      : null;
};

export const openDialog = (state: DialogState, action: any, active: ?any): Exact<DialogState> => {
  const newActive = [{
    id: action.productType.id,
    name: action.productType.name,
    products: _.flow(
      notArchived,
      sortByName,
      _.map(({id, name, mainVariantId, variants}) => ({
        id,
        name,
        mainVariantId,
        variants: _.flow(
          notArchived,
          sortByName,
          _.map(({id: variantId, name, itemNumber, hasRelated, sellingPrice}) => {
            const existingConfig = _.flow(
              _.values,
              _.find({productVariantId: variantId, parentId: null}),
            )(state.modifiedData);
            return ({
              id: variantId,
              name,
              itemNumber,
              hasRelated,
              productId: id,
              uuid: !!existingConfig ? existingConfig.uuid : uuid.v4(),
              sellingPrice,
            });
          }),
        )(variants),
        expanded: true,
      })),
    )(action.products),
  }];

  const modifiedData = {
    ...state.modifiedData,
    ...mapConfigEntries(state.modifiedData, null, null, active)(newActive),
  };

  return {
    ...state,
    error: null,
    isFetching: false,
    modifiedData,
    navigation: {
      active: newActive,
      previous: null,
      currentUuid: null,
      name: action.rootName,
      standardProducts: {
        [action.productType.id]: state.standardProductId,
      },
    },
    sharedChecked: getSharedChecked(newActive, modifiedData),
  };
};

export const navigateToVariant = (state: DialogState, action: any, active: ?any): Exact<DialogState> => {
  const existingData = state.modifiedData[action.variant.uuid];
  const hasExistingRelated = !!existingData && !!existingData.related.length;
  const newActive = hasExistingRelated
    ? existingData.related
    : mapRelated(state.config, action.variant);
  const modifiedData = {
    ...state.modifiedData,
    [action.variant.uuid]: {
      ...state.modifiedData[action.variant.uuid],
      related: newActive,
    },
    ...(hasExistingRelated
      ? {}
      : mapConfigEntries(
        state.modifiedData,
        !!existingData ? existingData.id : null,
        action.variant.uuid,
        active,
      )(newActive)),
  };

  return {
    ...state,
    error: null,
    isFetching: false,
    modifiedData,
    navigation: {
      active: newActive,
      currentUuid: action.variant.uuid,
      name: `${action.variant.productName} - ${action.variant.name}`,
      previous: state.navigation,
      standardProducts: existingData
        ? {
          ...existingData.standardProducts,
        }
        : {},
    },
    sharedChecked: getSharedChecked(newActive, modifiedData),
  };
};

export const popNavigation = (state: DialogState, action: any): Exact<DialogState> => {
  const navigation = popState(state.navigation, action.depth);
  return {
    ...state,
    navigation,
    sharedChecked: getSharedChecked(!!navigation ? navigation.active : [], state.modifiedData),
  };
};

export const toggleVariant = (state: DialogState, action: any, active: ?any): Exact<DialogState> => {
  const existingData = state.modifiedData[action.variant.uuid];
  const hasExistingRelated = !!existingData && !!existingData.related && !!existingData.related.length;
  const newRelated = hasExistingRelated
    ? existingData.related
    : mapRelated(state.config, action.variant);
  return {
    ...state,
    error: null,
    isFetching: false,
    modifiedData: {
      ...state.modifiedData,
      [action.variant.uuid]: {
        ...state.modifiedData[action.variant.uuid],
        expanded: action.expand,
        related: newRelated,
      },
      ...(hasExistingRelated
        ? {}
        : mapConfigEntries(
          state.modifiedData,
          !!existingData ? existingData.id : null,
          action.variant.uuid,
          active,
        )(newRelated)),
    },
  };
};

export const updateRelatedVariant = (state: DialogState, action: any): Exact<DialogState> => {
  const modifiedData = {
    ...state.modifiedData,
    [action.variant.uuid]: {
      ...state.modifiedData[action.variant.uuid],
      ...action.data,
    },
  };

  return {
    ...state,
    modifiedData,
    sharedChecked: getSharedChecked(!!state.navigation ? state.navigation.active : [], modifiedData),
  };
};

export const updateVariants = (state: DialogState, action: any): Exact<DialogState> => {
  const modifiedData = {
    ...state.modifiedData,
    ..._.flow(
      _.map(t => ({
        ...t,
        products: _.map(p => ({
          ...p,
          variants: _.filter(v => !!state.modifiedData[v.uuid], p.variants),
        }), t.products),
      })),
      _.flatMap(t => _.flatMap(p => _.map(v => ({
        ...state.modifiedData[v.uuid],
        ...(p.id === action.productId ? action.data : {}),
      }), p.variants), t.products)),
      _.keyBy(x => x.uuid),
    )(!!state.navigation ? state.navigation.active : []),
  };

  return {
    ...state,
    modifiedData,
    sharedChecked: getSharedChecked(!!state.navigation ? state.navigation.active : [], modifiedData),
  };
};

export const updateVariantsForType = (state: DialogState, action: any): Exact<DialogState> => {
  const modifiedData = {
    ...state.modifiedData,
    ..._.flow(
      _.map(t => ({
        ...t,
        products: _.map(p => ({
          ...p,
          variants: _.filter(v => !!state.modifiedData[v.uuid], p.variants),
        }), t.products),
      })),
      _.flatMap(t => _.flatMap(p => _.map(v => ({
        ...state.modifiedData[v.uuid],
        ...(t.id === action.productTypeId ? action.data : {}),
      }), p.variants), t.products)),
      _.keyBy(x => x.uuid),
    )(!!state.navigation ? state.navigation.active : []),
  };

  return {
    ...state,
    modifiedData,
    sharedChecked: getSharedChecked(!!state.navigation ? state.navigation.active : [], modifiedData),
  };
};

export const setStandardProduct = (state: DialogState, action: any): Exact<DialogState> => {
  const {navigation} = state;
  if (navigation) {
    if (!!navigation.currentUuid) {
      const parent = state.modifiedData[navigation.currentUuid];
      if (!!parent) {
        let modifiedData = {
          ...state.modifiedData,
          [navigation.currentUuid]: {
            ...parent,
            standardProducts: {
              ...parent.standardProducts,
              [action.productType.id]: action.product.id,
            },
          },
        };
        const matchingVariants = _.filter(x => _.some({uuid: x.uuid}, action.product.variants), modifiedData);

        if (!_.some(x => x.checked, matchingVariants)) {
          modifiedData = {
            ...modifiedData,
            ..._.flow(
              _.map(v => ({
                ...v,
                checked: true,
              })),
              _.keyBy(x => x.uuid),
            )(matchingVariants),
          };
        } else {
          const mainVariant = _.find({productVariantId: action.product.mainVariantId}, matchingVariants);
          if (!!mainVariant && !mainVariant.checked) {
            modifiedData = {
              ...modifiedData,
              [mainVariant.uuid]: {
                ...mainVariant,
                checked: true,
              },
            };
          }
        }
        return {
          ...state,
          modifiedData,
          navigation: {
            ...state.navigation,
            standardProducts: {
              ...(!!state.navigation ? state.navigation.standardProducts : {}),
              [action.productType.id]: action.product.id,
            },
          },
          sharedChecked: getSharedChecked(navigation.active, modifiedData),
        };
      }
    } else {
      const matchingVariants = _.filter(x => _.some({uuid: x.uuid}, action.product.variants), state.modifiedData);
      let modifiedData = state.modifiedData;
      if (!_.some(x => x.checked, matchingVariants)) {
        modifiedData = {
          ...modifiedData,
          ..._.flow(
            _.map(v => ({
              ...v,
              checked: true,
            })),
            _.keyBy('uuid'),
          )(matchingVariants),
        };
      } else {
        const mainVariant = _.find({productVariantId: action.product.mainVariantId}, matchingVariants);
        if (!!mainVariant && !mainVariant.checked) {
          modifiedData = {
            ...modifiedData,
            [mainVariant.uuid]: {
              ...mainVariant,
              checked: true,
            },
          };
        }
      }

      return {
        ...state,
        modifiedData,
        navigation: {
          ...state.navigation,
          standardProducts: {
            ...(!!state.navigation ? state.navigation.standardProducts : {}),
            [action.productType.id]: action.product.id,
          },
        },
        standardProductId: action.product.id,
        sharedChecked: getSharedChecked(navigation.active, modifiedData),
      };
    }
  }

  return state;
};

export const clearStandardProduct = (state: DialogState, action: any): Exact<DialogState> => {
  const {navigation} = state;
  if (navigation) {
    if (!!navigation.currentUuid) {
      const parent = state.modifiedData[navigation.currentUuid];
      if (!!parent) {
        return {
          ...state,
          modifiedData: {
            ...state.modifiedData,
            [navigation.currentUuid]: {
              ...parent,
              standardProducts: {
                ...parent.standardProducts,
                [action.productType.id]: null,
              },
            },
          },
          navigation: {
            ...state.navigation,
            standardProducts: {
              ...(!!state.navigation ? state.navigation.standardProducts : {}),
              [action.productType.id]: null,
            },
          },
        };
      }
    } else {
      return {
        ...state,
        navigation: {
          ...state.navigation,
          standardProducts: {
            ...(!!state.navigation ? state.navigation.standardProducts : {}),
            [action.productType.id]: null,
          },
        },
        standardProductId: null,
      };
    }
  }

  return state;
};

export const prepareDialog = (state: DialogState, action: any): Exact<DialogState> => {
  const config: ConfigEntry[] = _.flow(
    _.sortBy([x => x.id]),
    _.map(c => ({
      ...c,
      uuid: uuid.v4(),
    })),
  )((action.productSlot || {}).config);

  const configWithUuids = _.map(c => {
    const parent: ?ConfigEntry = !!c.parentId
      ? _.find({id: c.parentId}, config)
      : null;
    return {
      ...c,
      parentUuid: !!parent ? parent.uuid : null,
    };
  }, config);

  const modifiedData = _.flow(
    _.keyBy(x => x.uuid),
    _.mapValues(c => ({
      ...c,
      checked: true,
      expanded: false,
      related: [],
      standardProducts: _.flow(
        _.keyBy(x => x.productTypeId),
        _.mapValues(x => x.productId),
      )(c.standardProducts),
    })),
  )(configWithUuids);

  return {
    ...state,
    config: configWithUuids,
    modifiedData,
    standardProductId: !!action.productSlot
      ? action.productSlot.standardProductId
      : null,
    sharedChecked: getSharedChecked(!!state.navigation ? state.navigation.active : [], modifiedData),
  };
};

export const closeDialog = (state: DialogState, action: any): Exact<DialogState> => ({
  ...state,
  navigation: null,
  show: false,
});

export const resetDialog = (state: DialogState, action: any, active: ?any): Exact<DialogState> => ({
  ...state,
  modifiedData: _.flow(
    _.keyBy(x => x.uuid),
    _.mapValues(c => ({
      ...c,
      checked: true,
      expanded: false,
      related: [],
      standardProducts: _.flow(
        _.keyBy(x => x.productTypeId),
        _.mapValues(x => x.productId),
      )(c.standardProducts),
    })),
  )(state.config),
  navigation: null,
  show: false,
  standardProductId: !!active ? active.standardProductId : null,
  sharedChecked: false,
});

export const toggleProduct = (state: DialogState, action: any): Exact<DialogState> => ({
  ...state,
  navigation: !!state.navigation ? {
    ...state.navigation,
    active: _.map(p => ({
      ...p,
      expanded: p.id === action.product.id ? !p.expanded : p.expanded,
    }), state.navigation.active),
  } : state.navigation,
});

export const checkAllVariants = (state: DialogState, action: any): Exact<DialogState> => {
  const standardProducts = !!state.navigation
    ? state.navigation.standardProducts
    : {};
  const modifiedData = {
    ...state.modifiedData,
    ..._.flow(
      _.map(t => ({
        ...t,
        products: _.map(p => ({
          ...p,
          variants: _.filter(v => !!state.modifiedData[v.uuid], p.variants),
        }), t.products),
      })),
      _.flatMap(t => _.flatMap(p => _.map(v => ({
        ...state.modifiedData[v.uuid],
        checked: !!action.checked || (
          standardProducts[t.id] === p.id && v.id === p.mainVariantId
        ),
      }), p.variants), t.products)),
      _.keyBy(x => x.uuid),
    )(!!state.navigation ? state.navigation.active : []),
  };

  return {
    ...state,
    modifiedData,
    sharedChecked: getSharedChecked(!!state.navigation ? state.navigation.active : [], modifiedData),
  };
};

export const checkProduct = (state: DialogState, action: any): Exact<DialogState> => {
  const standardProducts = !!state.navigation
    ? state.navigation.standardProducts
    : {};
  const modifiedData = {
    ...state.modifiedData,
    ..._.flow(
      _.map(t => ({
        ...t,
        products: _.map(p => ({
          ...p,
          variants: _.filter(v => !!state.modifiedData[v.uuid], p.variants),
        }), t.products),
      })),
      _.flatMap(t => _.flatMap(p => _.map(v => ({
        ...state.modifiedData[v.uuid],
        checked: p.id === action.productId
          ? (!!action.checked || (
            standardProducts[t.id] === p.id && v.id === p.mainVariantId
          ))
          : state.modifiedData[v.uuid].checked,
      }), p.variants), t.products)),
      _.keyBy(x => x.uuid),
    )(!!state.navigation ? state.navigation.active : []),
  };

  return {
    ...state,
    modifiedData,
    sharedChecked: getSharedChecked(!!state.navigation ? state.navigation.active : [], modifiedData),
  };
};

const defaultConfigTemplateState: ConfigTemplateState = {
  active: null,
  error: null,
  isDirty: false,
  isFetching: false,
};

const defaultRoomsState: RoomsState = [];

const defaultRoomState: RoomState = {
  active: null,
  error: null,
  isDirty: false,
  isFetching: false,
};

export const defaultDialogState: DialogState = {
  config: [],
  error: null,
  isFetching: false,
  modifiedData: {},
  navigation: null,
  show: false,
  standardProductId: null,
  sharedChecked: false,
};

const defaultProductSlotState: ProductSlotState = {
  active: null,
  dialog: defaultDialogState,
  error: null,
  isDirty: false,
  isFetching: false,
  roomId: null,
};

const defaultRoomTypesState: RoomTypesState = [];

const defaultProductTypesState: ProductTypesState = [];

const defaultProductsState: ProductsState = [];

const defaultConfigTemplatesState: ConfigTemplatesState = [];

const defaultProjectsState: ProjectsState = [];

const defaultRoomTemplatesState: RoomTemplatesState = [];

const defaultState: State_ = {
  configTemplate: defaultConfigTemplateState,
  products: defaultProductsState,
  productSlot: defaultProductSlotState,
  productTypes: defaultProductTypesState,
  room: defaultRoomState,
  rooms: defaultRoomsState,
  roomTypes: defaultRoomTypesState,
  configTemplates: defaultConfigTemplatesState,
  projects: defaultProjectsState,
  roomTemplates: defaultRoomTemplatesState,
};

const configTemplateView = (state: State_ = defaultState, action: ConfigTemplateAction | FormAction): Exact<State> => {
  switch (action.type) {
    case GET_REQUEST:
      return {
        ...state,
        configTemplate: {
          ...state.configTemplate,
          active: null,
          error: null,
          isFetching: true,
        },
        products: defaultProductsState,
        productTypes: defaultProductTypesState,
        roomTypes: defaultRoomTypesState,
        projects: defaultProjectsState,
        roomTemplates: defaultRoomTemplatesState,
      };
    case CREATE_REQUEST:
    case UPDATE_REQUEST:
      return {
        ...state,
        configTemplate: {
          ...state.configTemplate,
          error: null,
          isFetching: true,
        },
      };
    case CREATE_FAILURE:
    case GET_FAILURE:
    case UPDATE_FAILURE:
      return {
        ...state,
        configTemplate: {
          ...state.configTemplate,
          error: action.error,
          isFetching: false,
        },
      };
    case CREATE_SUCCESS:
      return {
        ...state,
        configTemplate: {
          ...state.configTemplate,
          active: action.configTemplate,
          error: null,
          isDirty: false,
          isFetching: false,
        },
        rooms: action.configTemplate
          ? sortRoomList(action.configTemplate.rooms)
          : [],
      };
    case GET_SUCCESS: {
      return {
        ...state,
        configTemplate: {
          ...state.configTemplate,
          active: action.configTemplate,
          error: null,
          isDirty: false,
          isFetching: false,
        },
        products: action.products,
        productTypes: action.productTypes,
        rooms: action.configTemplate
          ? sortRoomList(action.configTemplate.rooms)
          : [],
        roomTypes: action.roomTypes,
        configTemplates: action.configTemplates || [],
        projects: action.projects,
        roomTemplates: action.roomTemplates,
      };
    }
    case UPDATE_SUCCESS:
      return {
        ...state,
        configTemplate: {
          ...state.configTemplate,
          active: action.configTemplate,
          error: null,
          isDirty: false,
          isFetching: false,
        },
      };
    case CLOSE_DIALOG:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: closeDialog(state.productSlot.dialog, action),
        },
      };
    case RESET:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: resetDialog(state.productSlot.dialog, action, state.productSlot.active),
        },
      };
    case NAVIGATE_TO_VARIANT_FAILURE:
    case OPEN_DIALOG_FAILURE:
    case TOGGLE_VARIANT_FAILURE:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: handleError(state.productSlot.dialog, action),
        },
      };
    case NAVIGATE_TO_VARIANT_REQUEST:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: navigateToVariantRequest(state.productSlot.dialog, action),
        },
      };
    case NAVIGATE_TO_VARIANT_SUCCESS:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: navigateToVariant(state.productSlot.dialog, action, state.productSlot.active),
        },
      };
    case OPEN_DIALOG_REQUEST:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: openDialogRequest(state.productSlot.dialog, action),
        },
      };
    case OPEN_DIALOG_SUCCESS:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: openDialog(state.productSlot.dialog, action, state.productSlot.active),
        },
      };
    case POP_DIALOG_NAVIGATION:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: popNavigation(state.productSlot.dialog, action),
        },
      };
    case SET_STANDARD_PRODUCT:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: setStandardProduct(state.productSlot.dialog, action),
        },
      };
    case CLEAR_STANDARD_PRODUCT:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: clearStandardProduct(state.productSlot.dialog, action),
        },
      };
    case TOGGLE_VARIANT_REQUEST:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: toggleVariantRequest(state.productSlot.dialog, action),
        },
      };
    case TOGGLE_VARIANT_SUCCESS:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: toggleVariant(state.productSlot.dialog, action, state.productSlot.active),
        },
      };
    case UPDATE_RELATED_VARIANT:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: updateRelatedVariant(state.productSlot.dialog, action),
        },
      };
    case TOGGLE_PRODUCT:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: toggleProduct(state.productSlot.dialog, action),
        },
      };
    case CHECK_ALL_VARIANTS:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: checkAllVariants(state.productSlot.dialog, action),
        },
      };
    case CHECK_PRODUCT:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: checkProduct(state.productSlot.dialog, action),
        },
      };
    case PRODUCT_SLOT_CREATE_REQUEST:
    case PRODUCT_SLOT_UPDATE_REQUEST:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          error: null,
          isFetching: true,
        },
      };
    case PRODUCT_SLOT_CREATE_FAILURE:
    case PRODUCT_SLOT_UPDATE_FAILURE:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          error: action.error,
          isFetching: false,
        },
      };
    case PRODUCT_SLOT_CREATE_SUCCESS: {
      const {
        productSlot,
        roomId: id,
      } = action;
      const currentRoom = {
        ..._.find({id}, state.rooms),
      };
      currentRoom.productSlots = currentRoom.productSlots ? sortByIndex([
        ...currentRoom.productSlots,
        productSlot,
      ]) : [productSlot];

      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          active: productSlot,
          dialog: prepareDialog(state.productSlot.dialog, action),
          error: null,
          isDirty: false,
          isFetching: false,
          roomId: id,
        },
        rooms: sortRoomList([
          ..._.reject({id: currentRoom.id}, state.rooms),
          currentRoom,
        ]),
      };
    }
    case PRODUCT_SLOT_UPDATE_SUCCESS: {
      const {
        productSlot,
      } = action;
      const currentRoom = {
        ..._.find(r => _.some({id: productSlot.id}, r.productSlots), state.rooms),
      };
      currentRoom.productSlots = sortByIndex([
        ..._.reject({id: productSlot.id}, currentRoom.productSlots),
        productSlot,
      ]);

      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          active: productSlot,
          dialog: prepareDialog(state.productSlot.dialog, action),
          error: null,
          isDirty: false,
          isFetching: false,
        },
        rooms: sortRoomList([
          ..._.reject({id: currentRoom.id}, state.rooms),
          currentRoom,
        ]),
      };
    }
    case ROOM_CREATE_REQUEST:
    case ROOM_UPDATE_REQUEST:
      return {
        ...state,
        room: {
          ...state.room,
          error: null,
          isFetching: true,
        },
      };
    case ROOM_CREATE_FAILURE:
    case ROOM_UPDATE_FAILURE:
      return {
        ...state,
        room: {
          ...state.room,
          error: action.error,
          isFetching: false,
        },
      };
    case ROOM_CREATE_SUCCESS:
      return {
        ...state,
        room: {
          ...state.room,
          active: action.room,
          error: null,
          isDirty: false,
          isFetching: false,
        },
        rooms: sortRoomList([
          ...state.rooms,
          action.room,
        ]),
      };
    case ROOM_UPDATE_SUCCESS:
      return {
        ...state,
        room: {
          ...state.room,
          active: action.room,
          error: null,
          isDirty: false,
          isFetching: false,
        },
        rooms: sortRoomList([
          ..._.reject({id: action.room.id}, state.rooms),
          action.room,
        ]),
      };
    case SELECT_PRODUCT_SLOT:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          active: action.productSlot || {uuid: uuid.v4(), advanced: false},
          dialog: prepareDialog(state.productSlot.dialog, action),
          roomId: action.roomId,
        },
        room: {
          ...state.room,
          active: null,
          error: null,
          isFetching: false,
        },
      };
    case SELECT_ROOM:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          active: null,
          dialog: defaultDialogState,
          error: null,
          isFetching: false,
          roomId: null,
        },
        room: {
          ...state.room,
          active: action.room || {},
        },
      };
    case SET_DIRTY:
      return {
        ...state,
        configTemplate: {
          ...state.configTemplate,
          isDirty: action.form === 'configTemplate'
            ? action.dirty
            : state.configTemplate.isDirty,
        },
        productSlot: {
          ...state.productSlot,
          isDirty: action.form === 'configTemplateProductSlot'
            ? action.dirty
            : state.productSlot.isDirty,
        },
        room: {
          ...state.room,
          isDirty: action.form === 'configTemplateRoom'
            ? action.dirty
            : state.room.isDirty,
        },
      };
    case UPDATE_VARIANTS:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: updateVariants(state.productSlot.dialog, action),
        },
      };
    case UPDATE_VARIANTS_FOR_TYPE:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          dialog: updateVariantsForType(state.productSlot.dialog, action),
        },
      };
    case ROOMS_UPDATE_REQUEST:
      return {
        ...state,
        room: {
          ...state.room,
          error: null,
          isFetching: true,
        },
      };
    case ROOMS_UPDATE_FAILURE:
      return {
        ...state,
        room: {
          ...state.room,
          error: action.error,
          isFetching: false,
        },
      };
    case ROOMS_UPDATE_SUCCESS:
      return {
        ...state,
        room: {
          ...state.room,
          active: !!state.room.active
            ? _.find({id: state.room.active.id}, action.rooms)
            : state.room.active,
          isDirty: false,
          isFetching: false,
          error: null,
        },
        rooms: sortRoomList(action.rooms),
      };
    case PRODUCT_SLOTS_UPDATE_REQUEST:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          error: null,
          isFetching: true,
        },
      };
    case PRODUCT_SLOTS_UPDATE_FAILURE:
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          error: action.error,
          isFetching: false,
        },
      };
    case PRODUCT_SLOTS_UPDATE_SUCCESS: {
      const productSlot = {
        ..._.find({id: action.productSlotId}, action.productSlots),
      } || state.productSlot.active;
      const currentRoom = {
        ..._.find({id: action.roomId}, state.rooms),
        productSlots: action.productSlots,
      };
      return {
        ...state,
        productSlot: {
          ...state.productSlot,
          active: productSlot,
          dialog: prepareDialog(state.productSlot.dialog, {
            ...action,
            productSlot,
          }),
          error: null,
          isDirty: false,
          isFetching: false,
        },
        rooms: sortRoomList([
          ..._.reject({id: action.roomId}, state.rooms),
          currentRoom,
        ]),
      };
    }
    case CLEAR:
      return defaultState;
    default:
      return state;
  }
};

export default configTemplateView;



// WEBPACK FOOTER //
// ./src/reducers/configTemplateView.js